import { HStack, Select, Skeleton, Stack, Text, createListCollection, useSelectContext } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { IndicatorsService, LibraryService } from '@/client';

function getUnitsQueryOptions({ slug, type }: { slug: string; type: string }) {
  return {
    queryKey: [`${type}_units`, { slug }],
    queryFn: async () => {
      if (type === 'suggestion') {
        return await LibraryService.readIndicatorUnits({ companySlug: slug });
      }
      return await IndicatorsService.readIndicatorUnits({ companySlug: slug });
    },
  };
}

type AllowedTypes = '' | 'suggestion';

const SelectValueItem = () => {
  const { t } = useTranslation();
  const select = useSelectContext();
  const items = select.selectedItems as Array<string>;

  if (!items || items.length === 0) {
    return <Text color="fg.muted">{t('indicator.columnsHeader.unit.placeholder')}</Text>;
  }

  return (
    <Select.ValueText placeholder={t('indicator.columnsHeader.unit.placeholder')}>
      <HStack>
        {items.length === 1 && (
          <Stack gap="0">
            <Text fontWeight="medium">{items[0]}</Text>
          </Stack>
        )}
        {items.length > 1 && <>{items.length} selected</>}
      </HStack>
    </Select.ValueText>
  );
};

export const SelectUnits = ({
  slug,
  value,
  onChange,
  type = '',
  label,
  unitListString,
  multiple = false,
}: {
  slug: string;
  value: string[];
  onChange: (value: Array<string>) => void;
  type?: AllowedTypes;
  label?: string;
  unitListString?: string; // Seperate units by comma
  multiple?: boolean;
}) => {
  const shouldFetch = !unitListString;

  const { data: unitsData, isLoading } = useQuery({
    ...getUnitsQueryOptions({ slug, type }),
    enabled: shouldFetch,
  });

  const units = unitListString
    ? unitListString
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean)
    : (unitsData?.data ?? []);

  const unitsCollection = createListCollection({
    items: units,
  });

  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Select.Root
          multiple={multiple}
          collection={unitsCollection}
          size="sm"
          value={value}
          onValueChange={(e) => onChange(e.value)}
          positioning={{ sameWidth: true }}
        >
          <Select.HiddenSelect />
          {label && <Select.Label>{label}</Select.Label>}
          <Select.Control>
            <Select.Trigger>
              <SelectValueItem />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.ClearTrigger />
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {unitsCollection.items.map((unit: string) => (
                <Select.Item item={unit} key={unit}>
                  {unit}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      )}
    </>
  );
};

export default SelectUnits;
